import React, { useState, useEffect } from "react";
import { ModalWithIcon } from "../../RfModal/ModalWithIcon";
import { getWorkspacePlanLimits } from "../../../../../clientServices/workspaceLimits";
import { DowngradePlanTargets, WorkspaceUsage } from "../../../../../state/types";
import { isCreditSystemEnabled } from "../../../../routes/workspace/utils";
import workspaceSvc from "../../../../../clientServices/workspaceSvc";
import { getIncludedCredits } from "../../../../utils/ubpUtils";
import { usePlanInfo } from "../../../hooks/usePlanInfo";
import { isFreePlan } from "@functions/workspacePlans";
import { usePaidPlanSubscription } from "../../../hooks/usePaidPlanSubscription";
import { Plan } from "@functions/plans/types";
import CheckBox from "../../CheckBox";
import { PubliclyAvailablePlans } from "@functions/featureAvailability";
import InlineAlert from "../../InlineAlert";

type CancelPlanModalProps = {
    usage: WorkspaceUsage;
    isOpen: boolean;
    onClose: () => void;
    onNext: () => void;
    isDowngrade: boolean;
    expiredTrial?: boolean;
    selectedDowngradePlan: DowngradePlanTargets;
};

export function CancelPlanModal({
    usage,
    isOpen,
    onClose,
    onNext,
    isDowngrade = false,
    expiredTrial = false,
    selectedDowngradePlan
}: CancelPlanModalProps) {
    const isCreditBased = expiredTrial ? true : isCreditSystemEnabled(usage);
    const newPlanLimits = getWorkspacePlanLimits(selectedDowngradePlan, isCreditBased);

    const { subscription } = usePaidPlanSubscription();

    const currentPlan = usage?.plan;

    const planContentData = usePlanInfo();
    let currentPlanName = planContentData[currentPlan]?.title || "Current Plan";
    if (expiredTrial) {
        currentPlanName = "Trial";
    }

    const currentPlanIsFree = isFreePlan(currentPlan);
    const currentPlanIsFreeNonSandbox = currentPlanIsFree && currentPlan !== Plan.LegacySandbox;

    const [loading, setLoading] = useState(false);
    const [acknowledged, setAcknowledged] = useState<Record<string, boolean>>({});
    const [modelCount, setModelCount] = useState<number>(0);

    const currentWorkspaceMembers = Object.keys(
        window?.workspacesById?.[window.currentWorkspace]?.members || {}
    ).length;
    const usersToRemove = currentWorkspaceMembers - newPlanLimits.collaborators;

    const rbacEnabled = expiredTrial ? true : !!workspaceSvc.hasRBACAddon(usage);
    const usedTrainCredits = usage?.train?.used;
    const remainingTrainCredits = usage?.train?.quota - usage?.train?.used;
    const apiCallsUsed = usage?.inference?.used;
    const trialLimits = getWorkspacePlanLimits(Plan.UbpPremiumTrial, true);
    const includedCredits = expiredTrial
        ? trialLimits.includedCredits
        : getIncludedCredits({ usage });

    const perCycleCopy = subscription?.isAnnual ? "yr" : "mo";

    const totalImages = (usage.images?.sources || 0) + (usage.images?.transforms || 0);

    const isCancellation =
        selectedDowngradePlan === Plan.Public || selectedDowngradePlan === Plan.LegacySandbox;
    const downgradeMode = isDowngrade && !isCancellation;

    type Callout = {
        title: string;
        id: string;
        criteriaMet?: boolean;
        count?: number;
        icon: string;
        description: React.ReactNode;
    };

    const usageBasedCallouts: Callout[] = [
        {
            title: "Users",
            id: "rbac enabled",
            criteriaMet: rbacEnabled && currentWorkspaceMembers > newPlanLimits.collaborators,
            count: currentWorkspaceMembers,
            icon: "far fa-user fa-fw",
            description: `You will lose access to review mode and labeler permissions. When your subscription ends, you will need to remove ${usersToRemove} ${usersToRemove > 1 ? "users" : "user"}.`
        },
        {
            title: "Users",
            id: "users > 3",
            criteriaMet: currentWorkspaceMembers > newPlanLimits.collaborators && !rbacEnabled,
            count: currentWorkspaceMembers,
            icon: "far fa-user fa-fw",
            description: `When your subscription ends, you will need to remove ${usersToRemove} ${usersToRemove > 1 ? "users" : "user"}.`
        },
        {
            title: "Private Projects",
            id: "private projects",
            criteriaMet:
                selectedDowngradePlan === Plan.Public && !usage?.features?.allowPrivateProjects,
            icon: "far fa-lock fa-fw",
            description: `You will have to make all projects public or lose access to them.`
        },
        {
            title: "Images",
            id: "images > 1,000",
            criteriaMet:
                usage?.images > newPlanLimits.sources && usage?.plan === Plan.LegacyStarter,
            count: usage?.images,
            icon: "far fa-images fa-fw",
            description: `Your Starter Plan includes ${newPlanLimits.sources.toLocaleString()} images. You will be limited to ${newPlanLimits.sources.toLocaleString()} images when your subscription ends.`
        },
        {
            title: "API Calls Used",
            id: "api calls >= 5,000",
            criteriaMet: apiCallsUsed > 5000 && usage?.plan === Plan.LegacyStarter,
            count: apiCallsUsed,
            icon: "far fa-sparkles fa-fw",
            description: `Free plans only include ${newPlanLimits.inferenceQuota.toLocaleString()} API calls/mo. Your Starter Plan includes ${usage?.inference?.quota?.toLocaleString()} API calls/${perCycleCopy} and discounted pricing.`
        },
        {
            title: "Models Trained",
            id: "models trained >= 10",
            criteriaMet: usedTrainCredits >= 10 && usage?.plan === Plan.LegacyStarter,
            count: usedTrainCredits,
            icon: "far fa-coins fa-fw",
            description: `You have trained ${usedTrainCredits?.toLocaleString()} models with your Starter Plan. You will have fewer credits when your subscription ends.`
        },
        {
            title: "Training Credits Remaining",
            id: "training credits remaining >= 5",
            criteriaMet:
                remainingTrainCredits >= 5 &&
                !(usedTrainCredits >= 10) &&
                usage?.plan === Plan.LegacyStarter, // don't include if Models Trained callout shown
            count: remainingTrainCredits,
            icon: "far fa-coins fa-fw",
            description: `You will forfeit all ${remainingTrainCredits.toLocaleString()} credits when your subscription ends.`
        },
        {
            title: "Lose Growth Plan Features",
            id: "growth downgrade",
            criteriaMet:
                usage.plan === Plan.UbpGrowth &&
                (selectedDowngradePlan === Plan.UbpBasic || selectedDowngradePlan === Plan.UbpCore),
            icon: "far fa-arrow-down fa-fw",
            description: `I understand that I will no longer have access to features like Role Based Access Control, Model Monitoring, and Priority Support.`
        },
        {
            title: "Fewer Credits",
            id: "credits",
            criteriaMet: isCreditBased,
            icon: "far fa-coins fa-fw",
            description: `I understand that I will only have access to ${newPlanLimits.includedCredits?.toLocaleString?.() ?? 0} credits/mo.`
        }
    ];

    let checkboxes: Callout[] = usageBasedCallouts.filter((c) => c.criteriaMet);

    if (!downgradeMode || isCancellation) {
        checkboxes.push({
            title: "Public Data",
            id: "public data",
            icon: "far fa-images fa-fw",
            description:
                "I understand that all data and models must be made public to continue using the platform."
        });
    }

    const keepCommercialLicense =
        selectedDowngradePlan === Plan.UbpBasic || selectedDowngradePlan === Plan.UbpCore;
    if (!keepCommercialLicense && !currentPlanIsFreeNonSandbox) {
        checkboxes.push({
            title: "No Commercial Deployment License",
            id: "commercial license",
            icon: "far fa-file-certificate fa-fw",
            description: (
                <>
                    I understand that I will no longer have a commercial license to use downloaded
                    models and weights.{" "}
                    <a href="https://www.roboflow.com/licensing" target="_blank" rel="noopener">
                        Learn More
                    </a>
                </>
            )
        });
    }

    if (
        checkboxes.length === 0 &&
        (usage?.plan === Plan.LegacyStarter || usage?.plan === Plan.UbpGrowth)
    ) {
        checkboxes.push({
            title: "Priority Support",
            id: "support",
            icon: "far fa-comments fa-fw",
            description: "You will lose hands-on support from our team of computer vision experts."
        });
    }

    const allChecked = checkboxes.every((c) => acknowledged[c.id]);

    const toggleAck = (id: string) => {
        setAcknowledged((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    useEffect(() => {
        async function fetchModelCount() {
            try {
                const response = await fetch(
                    `/query/models/countByWorkspace?workspaceId=${usage.id}`
                );
                if (response.ok) {
                    const data = await response.json();
                    const count = Object.values(data).reduce(
                        (acc: number, val: any) => acc + (val as number),
                        0
                    );
                    setModelCount(count);
                }
            } catch {
                setModelCount(0);
            }
        }
        if (isOpen) {
            fetchModelCount();
            setAcknowledged({});
        }
    }, [isOpen, usage.id]);

    // Fetch and display usage information via checkboxes

    const handleNext = async () => {
        setLoading(true);
        await onNext();
        setLoading(false);
    };

    // Warn legacy plans that they won't be able to re-enroll
    const isLegacyPlan = !PubliclyAvailablePlans.includes(currentPlan as Plan);

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            title={`Are you sure you want to ${isCancellation ? "cancel" : downgradeMode ? "downgrade" : "cancel"}?`}
            icon="far fa-exclamation-triangle"
            cancelButtonText="Nevermind"
            submitButtonText={
                isCancellation ? "Cancel Plan" : downgradeMode ? "Downgrade Plan" : "Cancel Plan"
            }
            type="warning"
            onSubmit={handleNext}
            submitButtonProps={{ loading, type: "red", disabled: !allChecked }}
            cancelButtonProps={{ disabled: !!loading }}
        >
            <div className="flex-co flex">
                <div className="flex w-full flex-1 flex-col items-center rounded-lg border border-gray-200">
                    {isLegacyPlan && (
                        <>
                            <InlineAlert level="danger" type="primary">
                                <div>
                                    The {currentPlanName} is no longer available.{" "}
                                    <span className="font-bold">
                                        You will not be able to enroll in this plan again.
                                    </span>{" "}
                                    If you choose to subscribe again later, you'll need to choose a
                                    publicly available plan, which may differ in pricing, limits,
                                    and features.
                                </div>
                            </InlineAlert>
                            <br />
                        </>
                    )}

                    <div className="flex w-full justify-center border-b border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-secondary">
                        Your workspace will be subject to the following changes.
                    </div>
                    <div className="flex w-full flex-col gap-4 p-4 text-sm text-gray-700">
                        {checkboxes.map((c) => (
                            <CheckBox
                                key={c.id}
                                checked={acknowledged[c.id] || false}
                                onChange={() => toggleAck(c.id)}
                                label={
                                    <span className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2">
                                            <i className={c.icon}></i>
                                            <span className="text-base font-medium">{c.title}</span>
                                        </span>
                                        {c.count && (
                                            <span className="text-base font-semibold">
                                                {c.count.toLocaleString()}
                                            </span>
                                        )}
                                    </span>
                                }
                                description={c.description}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </ModalWithIcon>
    );
}
